import { NavLink, Outlet, useLocation } from 'react-router-dom';

import type { Permission } from '../api/types.js';
import { ErrorBoundary } from './ErrorBoundary.js';

import { useSession } from '../auth/session-context.js';
import { WorkspaceStatus } from './WorkspaceStatus.js';
import { WorkspaceSwitcher } from './WorkspaceSwitcher.js';
import './AppShell.css';

/**
 * The authenticated Console shell (ARCHITECTURE.md §64, §65).
 *
 * Navigation is exactly what §64 names as SHIPPED, plus Moderator and Alerts —
 * both moved out of "Future" once the module they name was built (§98, §100).
 * Everything still under "Planned" is listed rather than linked, because the
 * prompt forbids UI implying capability that does not exist.
 *
 * `Alerts` carries a `permission`, which `Moderator` deliberately does not:
 * this render-time filter is a courtesy, exactly like the disabled buttons in
 * ModeratorPage (§4.2 — the API re-checks regardless of what this filtered).
 * Entitlement is NOT checked here: `/auth/me` carries no entitlement signal
 * for the Console to gate on, and the workspace's plan can change between one
 * request and the next in any case. `AlertsPage` itself asks the ONE
 * authoritative source — the API, on load — and shows the honest "not
 * included in this plan" state ModeratorPage already established for exactly
 * this situation, rather than this list guessing at an answer it cannot know.
 */

export const PRIMARY_NAV: readonly { to: string; label: string; permission?: Permission }[] = [
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/chat', label: 'Chat' },
  { to: '/moderator', label: 'Moderator' },
  { to: '/bot', label: 'Bot', permission: 'bot.view' },
  { to: '/alerts', label: 'Alerts', permission: 'alerts.view' },
  { to: '/widgets', label: 'Widgets', permission: 'widgets.view' },
  { to: '/connections', label: 'Connections' },
  { to: '/settings', label: 'Settings' },
];

/**
 * The modules §64 lists under "Future".
 *
 * Copied from ARCHITECTURE.md §64 and asserted against it by
 * `tests/console-help-accuracy.integration.test.ts`. `Automation` was missing
 * for the life of this list — §64 named six and the sidebar showed five —
 * which nobody noticed because a missing item looks like nothing at all.
 *
 * `Moderation` and `Alerts` left this list when their modules were built. The
 * document was edited first and this followed it: the test parses §64, so
 * loosening the test to accommodate the code would have inverted which one is
 * authoritative.
 */
export const PLANNED_NAV = ['Team', 'Billing', 'Analytics', 'Automation'] as const;

export function AppShell(): React.JSX.Element {
  const { me, signOut, can } = useSession();
  const location = useLocation();

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        Skip to main content
      </a>

      <div className="shell__brand">
        <span className="shell__wordmark">
          Connaro<span aria-hidden="true">.</span>
        </span>
      </div>

      <header className="shell__header">
        <WorkspaceSwitcher />

        {/*
          * Beside the workspace, because it is a fact ABOUT that workspace —
          * switching workspace changes it, and putting it next to the account
          * controls would imply otherwise.
          */}
        <WorkspaceStatus />

        <div className="shell__account">
          <span>{me?.user.displayName}</span>
          <button
            type="button"
            className="btn"
            onClick={() => {
              void signOut();
            }}
          >
            Sign out
          </button>
        </div>
      </header>

      <nav className="shell__nav" aria-label="Primary">
        <ul className="nav__list">
          {PRIMARY_NAV.filter((item) => item.permission === undefined || can(item.permission)).map(
            (item) => (
              <li key={item.to}>
                <NavLink className="nav__link" to={item.to}>
                  {item.label}
                </NavLink>
              </li>
            ),
          )}
        </ul>

        <p className="nav__section" id="planned-modules-heading">
          Planned
        </p>
        <ul className="nav__list" aria-labelledby="planned-modules-heading">
          {PLANNED_NAV.map((label) => (
            <li key={label}>
              {/*
               * Deliberately not links: these modules do not exist yet.
               *
               * No per-item badge either. The list already sits under a
               * "Planned" heading it is associated with by aria-labelledby,
               * so a badge on every row repeats the word visually and
               * announces it twice per item.
               *
               * It previously read "Soon", which was worse than redundant:
               * nothing here has a date, and "Soon" reads as a commitment to
               * anyone deciding whether to adopt Connaro now.
               */}
              <span className="nav__link nav__link--planned" aria-disabled="true">
                {label}
              </span>
            </li>
          ))}
        </ul>
      </nav>

      <main className="shell__main" id="main" tabIndex={-1}>
        {/*
         * Page-level, INSIDE the shell, so a crashing page keeps the sidebar,
         * the workspace switcher and sign-out. A boundary at the root would
         * catch the same error and take the navigation down with it, leaving
         * the operator with a message and no way to move off the broken page.
         *
         * Keyed on the path so navigating away clears it — a boundary that has
         * caught stays caught, and without this the chat error would still be
         * showing on Settings.
         */}
        <ErrorBoundary surface="this page" resetKey={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>
    </div>
  );
}

/** Label-only views of the navigation, for the §64 conformance test. */
export const PRIMARY_NAV_LABELS: readonly string[] = PRIMARY_NAV.map((item) => item.label);
export const PLANNED_NAV_LABELS: readonly string[] = [...PLANNED_NAV];
